/* Layout helpers shared by the editor and the server. */

import type { BuilderSettings, PricingConfig } from './config.js';
import type { ProjectDoc, Sheet } from './model.js';
import { itemBox } from './geometry.js';
import { rollLengthMm, priceSheets, type PriceOptions, type PriceResult } from './pricing.js';

/** Roll sheets grow and shrink with their content (rolling canvas). */
export function rollLength(sheet: Sheet, edgeMm: number, pricing: PricingConfig, transfer: ProjectDoc['transfer']): number {
  let bottom = 0;
  for (const it of sheet.items) { const b = itemBox(it); bottom = Math.max(bottom, b.y + b.h); }
  return rollLengthMm(bottom + edgeMm, bottom, pricing.transfers[transfer].byLength);
}

/** Recompute every roll sheet's length; returns the same doc if nothing changed. */
export function normaliseLengths(doc: ProjectDoc, builder: Pick<BuilderSettings, 'edgeMm'>, pricing: PricingConfig): ProjectDoc {
  let changed = false;
  const sheets = doc.sheets.map(s => {
    if (s.kind !== 'roll') return s;
    const L = rollLength(s, builder.edgeMm, pricing, doc.transfer);
    if (Math.abs(L - s.lengthMm) < 1e-6) return s;
    changed = true;
    return { ...s, lengthMm: L };
  });
  return changed ? { ...doc, sheets } : doc;
}

export function sheetsForPricing(doc: Pick<ProjectDoc, 'sheets'>) {
  return doc.sheets.map(s => ({ kind: s.kind, fixedKey: s.fixedKey, widthMm: s.widthMm, lengthMm: s.lengthMm }));
}

export function priceProject(doc: ProjectDoc, pricing: PricingConfig, opts: PriceOptions = {}): PriceResult {
  return priceSheets(sheetsForPricing(doc), doc.transfer, pricing, opts);
}

/** Total film and artwork figures for headlines. */
export function sheetStats(sheet: Sheet) {
  const film = sheet.widthMm * sheet.lengthMm;
  let art = 0;
  for (const it of sheet.items) art += it.widthMm * it.heightMm;
  return { filmMm2: film, artMm2: art, utilisation: film ? art / film : 0, count: sheet.items.length };
}
